import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './components/layout/mobile-pages.css'
import App from './App.tsx'
import { installFetchAuthGuard } from './auth/fetchAuthGuard'
import { runDiagnostics } from './utils/diagnostics'
import { ensureCsrfToken } from './api/client'
// @ts-ignore - virtual module generated by vite-plugin-pwa
import { registerSW } from 'virtual:pwa-register';

// ── AC-MCP: Registrar contratos semânticos no boot ──
import { AcRegistry } from './contracts/ac-registry'
import { ALUNOS_CONTRACT } from './contracts/alunos.contract'

AcRegistry.register(ALUNOS_CONTRACT);
// Futuros contratos: AcRegistry.register(TURMAS_CONTRACT); etc.

console.log(`[AcRegistry] ${AcRegistry.size} campos em ${AcRegistry.listDomains().length} domínio(s): ${AcRegistry.listDomains().join(', ')}`);

import { LOCAL_BUILD_METADATA } from './features/telemetry/localBuildMetadata';

// ── Auto-Purge de Cache de Versão do App ──
// Ao detectar que uma nova versão do frontend foi implantada,
// limpa os caches do CacheStorage e força atualização limpa do Service Worker.
const APP_VERSION = LOCAL_BUILD_METADATA.version;
try {
  const cachedVersion = localStorage.getItem('ac_app_version');
  if (cachedVersion && cachedVersion !== APP_VERSION) {
    console.warn(`[AutoPurge] Nova versão detectada (${cachedVersion} -> ${APP_VERSION}). Purgando caches legados do navegador...`);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const reg of registrations) reg.unregister();
      }).catch((e) => console.error('[AutoPurge] Erro ao desregistrar SW:', e));
    }
    if ('caches' in window) {
      caches.keys().then((keys) => {
        return Promise.all(
          keys.filter((key) => !key.startsWith('map-tiles-')).map((key) => caches.delete(key))
        );
      }).catch((e) => console.error('[AutoPurge] Erro ao limpar CacheStorage:', e));
    }
  }
  localStorage.setItem('ac_app_version', APP_VERSION);
} catch { /* noop */ }

const params = new URLSearchParams(window.location.search);

// Cockpit embed: aplicar ANTES do 1º paint (evita fade/splash no primeiro load do iframe)
try {
  let embed = sessionStorage.getItem('ac_cockpit_embed') === '1'
    || sessionStorage.getItem('ac_skip_splash') === '1';
  const imp = params.get('impersonate');
  if (imp) {
    try {
      const data = JSON.parse(decodeURIComponent(imp));
      if (data?.skip_splash || data?.from_cockpit) embed = true;
    } catch { /* noop */ }
  }
  if (params.get('from_cockpit') === '1' || params.get('skip_splash') === '1') embed = true;
  if (embed) {
    sessionStorage.setItem('ac_cockpit_embed', '1');
    sessionStorage.setItem('ac_skip_splash', '1');
    document.documentElement.classList.add('ac-cockpit-embed');
  }
} catch { /* noop */ }

// CSRF no boot (evita POST/DELETE com token ausente em cross-subdomain)
void ensureCsrfToken();

if (params.get('check') === '1') {
  runDiagnostics();
} else {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

// ── Capturar beforeinstallprompt GLOBALMENTE no boot ──
// O evento dispara UMA VEZ quando o browser determina que o app e instalavel.
// Se nao capturar aqui, o PwaInstaller pode montar depois e perder o evento.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  (window as any).__pwaInstallPrompt = e;
  console.log('[PWA] beforeinstallprompt capturado globalmente');
});


// Register main PWA service worker — atualização imediata durante splash e em background
if ('serviceWorker' in navigator) {
  let refreshing = false;

  // Ouvinte para recarregar a página assim que o novo Service Worker assumir o controle
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    console.log('%c[PWA-Update]%c Novo Service Worker assumiu o controle. Recarregando aplicação...', 'color: #38bdf8; font-weight: bold;');
    window.location.reload();
  });

  const updateSW = registerSW({
    onOfflineReady() {
      console.log(
        '%c[PWA-Cache]%c Aplicação carregada e rodando via Cache local (Pronto offline!).',
        'color: #34d399; font-weight: bold; background: #064e3b; padding: 2px 5px; border-radius: 3px;',
        'color: #94a3b8;'
      );
    },
    onNeedRefresh() {
      // Proteção anti-loop: só recarrega se não recarregou nos últimos 10s
      const lastUpdate = sessionStorage.getItem('pwa-update-ts');
      const now = Date.now();
      if (lastUpdate && now - Number(lastUpdate) < 10000) {
        console.log('%c[PWA-Update]%c Atualização já aplicada recentemente, ignorando.', 'color: #fbbf24; font-weight: bold;', 'color: #94a3b8;');
        return;
      }
      console.log(
        '%c[PWA-Update]%c Nova versão detectada! Aplicando atualização durante o carregamento...',
        'color: #fbbf24; font-weight: bold; background: #78350f; padding: 2px 5px; border-radius: 3px;',
        'color: #e2e8f0;'
      );
      sessionStorage.setItem('pwa-update-ts', String(now));
      updateSW(true); // skipWaiting + reload imediato
    },
  });

  // Função para forçar o navegador a verificar se o Service Worker mudou no servidor
  const checkForSwUpdate = async () => {
    try {
      if (navigator.onLine) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
        }
      }
    } catch {
      // Falhas offline são ignoradas silenciosamente
    }
  };

  // 1. Ao retornar do background no celular (desbloqueio / troca de app no iPhone)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForSwUpdate();
    }
  });

  // 2. Ao restaurar a página de cache de navegação (bfcache no iOS Safari)
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      void checkForSwUpdate();
    }
  });

  // 3. Polling a cada 60 segundos enquanto a aba estiver aberta
  setInterval(() => {
    if (document.visibilityState === 'visible') {
      void checkForSwUpdate();
    }
  }, 60000);
}
